// The changes of a commit, a compare, a merge diff or a dry run as the pages show them (spec/dashboard.md section 12.1):
// grouped by kind (templates, enums, documents by template, strings, assets, products, Addressables, extensions, other
// files), one line per changed leaf path from `before` / `after` (section 20.6 of the API), and the link to the entity's
// editor on the branch.
import type { Json, TreeChange } from '../api/types';
import type { DocumentLike } from '../content/format';
import { routes } from '../shell/routes';
import { diffLeaves } from '../workspace/paths';

/**
 * A change with the content `include=content` attaches (absent without the parameter, and beyond the 500-change cap). The
 * generated type leaves the two sides `unknown` (the API ships any JSON); the pages pass the DTOs as they come.
 */
export type ContentChange = Omit<TreeChange, 'before' | 'after'> & { before?: unknown; after?: unknown };

export type ChangeKind = 'template' | 'enum' | 'document' | 'localization_string' | 'asset' | 'product' | 'addressables' | 'localization_settings' | 'extension' | 'other';

export interface ChangeGroup {
  key: string;
  kind: ChangeKind;
  /** "Templates", "Documents · SmartObjects.Item", … */
  title: string;
  changes: ContentChange[];
}

const KIND_TITLE: Record<ChangeKind, string> = {
  template: 'Templates',
  enum: 'Enums',
  document: 'Documents',
  localization_string: 'Localization strings',
  asset: 'Assets',
  product: 'Products',
  addressables: 'Addressables',
  localization_settings: 'Localization settings',
  extension: 'Extensions',
  other: 'Other files',
};

const KIND_ORDER: ChangeKind[] = ['template', 'enum', 'document', 'localization_string', 'asset', 'product', 'addressables', 'localization_settings', 'extension', 'other'];

/** The kind of a change: the server's wire kind, with extension scripts told apart from the other untyped files. */
export function kindOf(c: TreeChange): ChangeKind {
  const k = c.kind as ChangeKind | null | undefined;
  if (k && k in KIND_TITLE) return k;
  if (c.path.startsWith('extensions/')) return 'extension';
  return 'other';
}

/** The extension's class name from its file path (`extensions/Name.js` / `.json`). */
export function extensionNameOf(path: string): string | null {
  const m = /^extensions\/([^/]+)\.(js|json)$/.exec(path);
  return m ? m[1]! : null;
}

/** Groups by kind in the fixed order, documents by template name, keeping the path order inside a group. */
export function groupChanges(changes: ContentChange[]): ChangeGroup[] {
  const groups = new Map<string, ChangeGroup>();
  for (const c of changes) {
    const kind = kindOf(c);
    const key = kind === 'document' ? `document:${c.name ?? '?'}` : kind;
    let g = groups.get(key);
    if (!g) {
      g = { key, kind, title: kind === 'document' ? `${KIND_TITLE.document} · ${c.name ?? 'unknown template'}` : KIND_TITLE[kind], changes: [] };
      groups.set(key, g);
    }
    g.changes.push(c);
  }
  return Array.from(groups.values()).sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.title.localeCompare(b.title));
}

export interface LeafDiff {
  path: string;
  before: unknown;
  after: unknown;
}

/** One row per changed leaf (the whole value as `$` when it is not an object); none without content. */
export function leafDiffs(c: ContentChange): LeafDiff[] | null {
  if (c.before === undefined && c.after === undefined) return null;
  return diffLeaves((c.before ?? null) as Json, (c.after ?? null) as Json);
}

/** `true` when the server attached the content of this change (either side present, `null` included). */
export function hasContentSides(c: ContentChange): boolean {
  return c.before !== undefined || c.after !== undefined;
}

export function hasContent(c: ContentChange): boolean {
  return c.before !== undefined || c.after !== undefined;
}

export interface ChangeLinkContext {
  org: string;
  game: string;
  branch: string;
  /** a document by id, when the catalog knows it (its template names the route) */
  resolve?: (id: string) => DocumentLike | undefined;
}

/** Where the entity of a change is edited on the branch; null when there is no page for it. */
export function changeHref(c: ContentChange, ctx: ChangeLinkContext): string | null {
  const kind = kindOf(c);
  switch (kind) {
    case 'document': {
      const template = c.name ?? ctx.resolve?.(c.id ?? '')?.template;
      if (!template || !c.id) return null;
      return `${routes.documents(ctx.org, ctx.game, ctx.branch, template)}?doc=${encodeURIComponent(c.id)}`;
    }
    case 'template':
      return c.name ? routes.template(ctx.org, ctx.game, ctx.branch, c.name) : routes.templates(ctx.org, ctx.game, ctx.branch);
    case 'enum':
      return c.name ? routes.enumEditor(ctx.org, ctx.game, ctx.branch, c.name) : routes.enums(ctx.org, ctx.game, ctx.branch);
    case 'localization_string':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'localization/strings') + (c.id ? `?search=${encodeURIComponent(c.id)}` : '');
    case 'localization_settings':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'localization/settings');
    case 'asset':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'assets/files') + (c.id ? `?asset=${encodeURIComponent(c.id)}` : '');
    case 'addressables':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'assets/addressables');
    case 'product':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'products');
    case 'extension': {
      const name = extensionNameOf(c.path);
      return name ? routes.branch(ctx.org, ctx.game, ctx.branch, `extensions/${encodeURIComponent(name)}`) : null;
    }
    default:
      return null;
  }
}

/** The entity's name for a row: the resolved document label when known, else the change's name / extension name / path. */
export function changeTitle(c: ContentChange, label?: (doc: DocumentLike) => string, resolve?: (id: string) => DocumentLike | undefined): string {
  const kind = kindOf(c);
  if (kind === 'document') {
    const doc = c.id ? resolve?.(c.id) : undefined;
    if (doc && label) return label(doc);
    // the content of the change names the document when the catalog cannot (a deleted one, another branch)
    const data = (obj(c.after) ?? obj(c.before))?.data;
    const name = obj(data)?.name ?? obj(data)?.title;
    return typeof name === 'string' && name ? `${name} [${(c.id ?? '').slice(0, 8)}]` : (c.id ?? c.path);
  }
  if (kind === 'extension') return extensionNameOf(c.path) ?? c.path;
  if (kind === 'localization_string') {
    const s = obj(c.after) ?? obj(c.before);
    const group = s?.group;
    const key = s?.key;
    if (typeof group === 'string' && typeof key === 'string') return `${group}/${key}`;
  }
  if (kind === 'asset' || kind === 'product') {
    const s = obj(c.after) ?? obj(c.before);
    if (typeof s?.name === 'string') return s.name;
    if (typeof s?.itemId === 'string') return s.itemId;
  }
  return c.name ?? c.path;
}

export const STATUS_WORD: Record<string, string> = { added: 'added', modified: 'modified', deleted: 'deleted', renamed: 'renamed', copied: 'copied', typechanged: 'changed', unmodified: 'unchanged' };

function obj(v: unknown): Record<string, Json> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, Json>) : null;
}
